import { ActionType, ConditionType } from '@src/components/generated/graphql';
import { CAMERA_FLY_PRESET_TYPES } from '../../types';
import { INTERACTION_TYPES } from 'util/constants';

export const getActionTypeLabel = (actionType: ActionType): string => {
  switch (actionType) {
    case ActionType.MoveMainCamera:
      return 'Move Main Camera';
    case ActionType.RotateMainCamera:
      return 'Rotate Main Camera';
    case ActionType.SwitchBetweenCameras:
      return 'Switch Camera';
    case ActionType.Move:
      return 'Move';
    case ActionType.Rotate:
      return 'Rotate';
    case ActionType.Scale:
      return 'Scale';
    case ActionType.PlayAnimation:
      return 'Play Animation';
    case ActionType.PlayAudio:
      return 'Play Audio';
    case ActionType.PlayCutscene:
      return 'Play Cutscene';
    case ActionType.DisplayText:
      return 'Display Text';
    case ActionType.ShowModal:
      return 'Show Modal';
    case ActionType.Hide:
      return 'Hide';
    case ActionType.Show:
      return 'Show';
    case ActionType.SpawnNpc:
      return 'Spawn NPC';
    case ActionType.IncrementCounter:
      return 'Increment Counter';
    case ActionType.PlayNpcAction:
      return 'Play NPC Action';
    case ActionType.Custom:
      return 'Custom';
    default:
      return 'Action';
  }
};

export const getFlyPresetLabel = (
  presetType: CAMERA_FLY_PRESET_TYPES
): string => {
  switch (presetType) {
    case CAMERA_FLY_PRESET_TYPES.CIRCLE:
      return 'Circle';
    case CAMERA_FLY_PRESET_TYPES.DRONIE:
      return 'Dronie';
    case CAMERA_FLY_PRESET_TYPES.HELIX:
      return 'Helix';
    case CAMERA_FLY_PRESET_TYPES.ROCKET:
      return 'Rocket';
    default:
      return '';
  }
};

export const getActionNodeLabel = (data: any): string => {
  if (!data || !data.type) {
    return 'Action';
  }

  const actionType = data.type;

  if (
    actionType === ActionType.Move &&
    data.params?.position?.x === 0 &&
    data.params?.position?.y === 0 &&
    data.params?.position?.z === 0 &&
    data.params?.easing === 'linear' &&
    data.delay === 0 &&
    data.duration === 0
  ) {
    return 'Action';
  }

  const actionLabel = getActionTypeLabel(actionType);

  if (actionType === ActionType.MoveMainCamera && data.params?.flyPresetType) {
    const presetLabel = getFlyPresetLabel(data.params.flyPresetType);
    return `${actionLabel} ${presetLabel}`;
  }

  return actionLabel;
};

export const getConditionTypeLabel = (conditionType: ConditionType): string => {
  switch (conditionType) {
    case ConditionType.SceneLoaded:
      return 'Scene Loaded';
    case ConditionType.TimeDelay:
      return 'Time Delay';
    case ConditionType.EventCompleted:
      return 'Event Completed';
    case ConditionType.Interaction:
      return 'Interaction';
    case ConditionType.CounterReached:
      return 'Counter Reached';
    case ConditionType.Collision:
      return 'Collision';
    case ConditionType.Custom:
      return 'Custom';
    default:
      return 'Condition';
  }
};

export const getInteractionTypeLabel = (interactionType: string): string => {
  switch (interactionType) {
    case INTERACTION_TYPES.CLICK:
      return 'Click';
    case INTERACTION_TYPES.PROXIMITY:
      return 'Proximity';
    case INTERACTION_TYPES.COLLISION:
      return 'Collision';
    case INTERACTION_TYPES.CUSTOM:
      return 'Custom';
    default:
      return '';
  }
};

export const getConditionNodeLabel = (data: any): string => {
  if (!data || !data.type) {
    return 'Condition';
  }

  const conditionType = data.type;

  if (conditionType === ConditionType.Interaction) {
    const isNewNode =
      data.params?.targetId === '' &&
      data.params?.interactionType === INTERACTION_TYPES.COLLISION &&
      Object.keys(data.params || {}).length <= 2;

    if (isNewNode) {
      return 'Condition';
    }

    const conditionLabel = getConditionTypeLabel(conditionType);
    if (data.params?.interactionType) {
      const interactionLabel = getInteractionTypeLabel(
        data.params.interactionType
      );
      if (interactionLabel) {
        return `${conditionLabel} ${interactionLabel}`;
      }
    }
    return conditionLabel;
  }

  const conditionLabel = getConditionTypeLabel(conditionType);
  return conditionLabel;
};
